import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  IconButton,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Text,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  EmailTemplatesApi,
  type EmailTemplateRow,
  type EmailTemplatePatch,
} from "../api/emailTemplates";
import { fmtDate } from "../utils/datetime";
import logger from "../analytics/logger";
import EmailPreview from "../components/EmailPreview";
import JinjaTagHelp from "../components/JinjaTagHelp";
import { useEmailTemplateVars } from "../hooks/useEmailTemplateVars";
import { buildVocab } from "../lib/jinjaHighlight";
import { apiErrorMessage } from "../lib/apiErrorMessage";

function eq(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function buildPatch(orig: EmailTemplateRow, draft: EmailTemplateRow): EmailTemplatePatch {
  const patch: EmailTemplatePatch = {};
  if (!eq(orig.subject, draft.subject)) patch.subject = draft.subject;
  if (!eq(orig.description, draft.description)) patch.description = draft.description;
  if (!eq(orig.active, draft.active)) patch.active = draft.active;
  return patch;
}

export default function EmailTemplateDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const [orig, setOrig] = useState<EmailTemplateRow | null>(null);
  const [draft, setDraft] = useState<EmailTemplateRow | null>(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [htmlLoading, setHtmlLoading] = useState(false);
  const { vocab: catalog } = useEmailTemplateVars();
  const vocab = useMemo(() => buildVocab(catalog, orig?.name ?? null), [catalog, orig?.name]);

  const load = () => {
    setLoading(true);
    setError(null);
    EmailTemplatesApi.get(id)
      .then((d) => {
        setOrig(d);
        setDraft(d);
      })
      .catch((e) => {
        logger.error("email_template_get_failed", e);
        setError(e?.response?.data?.error ?? "Failed to load template");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    void (async () => {
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!orig?.template_location) return;
    let active = true;
    (async () => {
      setHtmlLoading(true);
      try {
        const h = await EmailTemplatesApi.getHtml(id);
        if (!active) return;
        setHtml(h);
      } catch (e) {
        if (!active) return;
        logger.error("email_template_html_failed", e);
        setHtml(null);
      } finally {
        if (active) setHtmlLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orig?.template_location]);

  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const cancel = () => {
    if (orig) setDraft(orig);
    clearFile();
    setEditing(false);
    setError(null);
  };

  const back = () => {
    cancel();
    navigate("/email-templates");
  };

  const save = async () => {
    if (!orig || !draft) return;
    const patch = buildPatch(orig, draft);
    setSaving(true);
    setError(null);
    try {
      // Optionally replace the HTML body: upload to S3, then patch the key.
      if (file) {
        const { uploadUrl, key } = await EmailTemplatesApi.getUploadUrl("application/zip");
        const put = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": "application/zip" },
          body: file,
        });
        if (!put.ok) throw new Error(`upload failed: ${put.status}`);
        patch.template_location = key;
      }
      if (Object.keys(patch).length === 0) {
        clearFile();
        setEditing(false);
        return;
      }
      const updated = await EmailTemplatesApi.update(id, patch);
      setOrig(updated);
      setDraft(updated);
      clearFile();
      setEditing(false);
    } catch (e: unknown) {
      logger.error("email_template_update_failed", e);
      setError(apiErrorMessage(e, "Failed to save changes"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner />
      </Box>
    );
  }
  if (!orig || !draft) {
    return (
      <Box p={6}>
        <Button leftIcon={<ArrowBackIcon />} variant="ghost" onClick={back} mb={4}>
          Back
        </Button>
        <Alert status="error">
          <AlertIcon />
          <AlertDescription>{error ?? "Template not found"}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <Flex align="center" justify="space-between" mb={4}>
        <HStack>
          <IconButton aria-label="Back" icon={<ArrowBackIcon />} variant="ghost" onClick={back} />
          <Heading size="lg">{orig.name}</Heading>
        </HStack>
        <HStack>
          {editing ? (
            <>
              <Button size="sm" variant="ghost" onClick={cancel} isDisabled={saving}>
                Cancel
              </Button>
              <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving}>
                Save
              </Button>
            </>
          ) : (
            <HStack>
              <Text fontSize="sm" color="gray.600">
                Edit
              </Text>
              <Switch isChecked={editing} onChange={(e) => setEditing(e.target.checked)} />
            </HStack>
          )}
        </HStack>
      </Flex>

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        <Stack spacing={3}>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Name (send key — not editable)
            </Text>
            <Text>{orig.name}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Description
            </Text>
            {editing ? (
              <Input
                size="sm"
                value={draft.description ?? ""}
                onChange={(e) =>
                  setDraft({ ...draft, description: e.target.value || null })
                }
              />
            ) : (
              <Text>{orig.description || "—"}</Text>
            )}
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Active
            </Text>
            {editing ? (
              <Switch
                isChecked={draft.active}
                onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
              />
            ) : (
              <Text>{orig.active ? "Yes" : "No"}</Text>
            )}
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Created
            </Text>
            <Text>{fmtDate(orig.created_at)}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Updated
            </Text>
            <Text>{fmtDate(orig.updated_at)}</Text>
          </Box>
          {editing && (
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>
                Replace with zipped HTML export (optional)
              </Text>
              <Input
                ref={fileRef}
                size="sm"
                type="file"
                accept=".zip,application/zip"
                pt={1}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </Box>
          )}
        </Stack>

        {/* The tags THIS template actually receives, so authors don't reach for
            one from another lane that renders blank. */}
        <JinjaTagHelp catalog={catalog} templateName={orig.name} />
      </SimpleGrid>

      <Box mt={6}>
        <EmailPreview
          html={html}
          loading={htmlLoading}
          subject={editing ? draft.subject : orig.subject}
          subjectEditor={
            editing ? (
              <Input
                size="sm"
                value={draft.subject}
                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              />
            ) : undefined
          }
          vocab={vocab}
        />
      </Box>
    </Box>
  );
}
